/* eslint-disable no-unused-vars */
import { appendHeaders, apiUrl, fetchWithAuth } from "./index";
import { getDashboardApiToken } from "../apiSessionService";
import type {
  CreateSaccoPayload,
  StageCollectionSummary,
  StageCollectionRevenue,
  StageCollectionTopStages,
  AddMatatuPayload,
  MatatuRecord,
} from "../../types/sacco";

/** GET /sacco/{sacco_id} — fetch a single sacco by id. */
export async function getSaccoById(saccoId: string): Promise<Record<string, unknown>> {
  const response = await fetchWithAuth(
    `${apiUrl}/sacco/profile/${encodeURIComponent(saccoId)}`,
    { method: "GET", headers: appendHeaders() },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message ||
        `Failed to fetch sacco (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data as Record<string, unknown>;
}

/** POST /stage-collection-matatu/{sacco_id}/bulk-upload — upload matatus CSV. */
export async function bulkUploadMatatus(
  saccoId: string,
  file: File,
): Promise<Record<string, unknown>> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetchWithAuth(
    `${apiUrl}/stage-collection-matatu/${encodeURIComponent(saccoId)}/bulk-upload`,
    {
      method: "POST",
      headers: { Authorization: "Bearer " + getDashboardApiToken() },
      body: formData,
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message ||
        `Bulk upload failed (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data as Record<string, unknown>;
}

/** PATCH /sacco/profile/{sacco_id} — update sacco routes and stages. */
export async function updateSaccoProfile(
  saccoId: string,
  payload: { routes: string[]; stages: string[] },
): Promise<void> {
  const response = await fetchWithAuth(
    `${apiUrl}/sacco/profile/${encodeURIComponent(saccoId)}`,
    {
      method: "PATCH",
      headers: appendHeaders(),
      body: JSON.stringify(payload),
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message ||
        `Failed to update sacco profile (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
}

/** POST /stage-collection-matatu/{sacco_id} — add a single matatu to a sacco. */
export async function addSaccoMatatu(
  saccoId: string,
  payload: AddMatatuPayload,
): Promise<void> {
  const response = await fetchWithAuth(
    `${apiUrl}/stage-collection-matatu/${encodeURIComponent(saccoId)}`,
    {
      method: "POST",
      headers: appendHeaders(),
      body: JSON.stringify(payload),
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message ?? "Failed to add matatu.");
  }
}

/** GET /stage-collection-matatu/{sacco_id}?page=&size= — fetch matatus for a sacco. */
export async function fetchMatatusBySaccoId(
  saccoId: string,
  page?: number,
  size?: number,
): Promise<MatatuRecord[]> {
  const params = new URLSearchParams();
  if (page !== undefined) params.set("page", String(page));
  if (size !== undefined) params.set("size", String(size));
  const query = params.toString();
  const response = await fetchWithAuth(
    `${apiUrl}/stage-collection-matatu/${encodeURIComponent(saccoId)}${query ? `?${query}` : ""}`,
    { method: "GET", headers: appendHeaders() },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message ||
        `Failed to fetch matatus (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return (Array.isArray(data) ? data : (data as { content?: MatatuRecord[] })?.content ?? []) as MatatuRecord[];
}

/** POST /sacco — create a new sacco. */
export async function createSacco(payload: CreateSaccoPayload) {
  const response = await fetchWithAuth(`${apiUrl}/sacco`, {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message ||
        `Failed to create sacco (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
}

export function fetchOwnerMatatus(owner) {
  return fetchWithAuth(apiUrl + "/sacco/member/matatu/query", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(owner),
  }).then(function (response) {
    return response.json();
  });
}

// get all crew for sacco
export function fetchCrew(saccoName) {
  return fetchWithAuth(apiUrl + "/sacco/crew/" + saccoName, {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

export function fetchaccounts(saccoName) {
  return fetchCrew(saccoName);
}

// fetch list of matatus based on sacco
export function fetchMatatus(saccoName) {
  return fetchWithAuth(apiUrl + "/sacco/matatu/" + saccoName, {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (_res) {
    return _res.json();
  });
}

export async function registerToSacco(_user) {
  const response = await fetchWithAuth(apiUrl + "/sacco/member", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(_user),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      data?.message || `Sacco registration failed (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
}

/** GET /sacco — list saccos (for dropdowns, admin screens). */
export async function fetchSaccos() {
  const response = await fetchWithAuth(`${apiUrl}/sacco`, {
    method: "GET",
    headers: appendHeaders(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message ||
        `Failed to load saccos (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
}

export function fetchSaccoMembers(saccoId: string) {
  return fetchWithAuth(
    `${apiUrl}/sacco/member?saccoId=${encodeURIComponent(saccoId)}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then((res) => res.json());
}

// edit profile details
export function editProfile(editProfile) {
  return fetchWithAuth(apiUrl + "/sacco/member", {
    method: "PATCH",
    headers: appendHeaders(),
    body: JSON.stringify(editProfile),
  }).then(function (response) {
    return response.json();
  });
}

// fetch matatu by licenseplate
export function fetchMatatuByPlate(walletId) {
  return fetchWithAuth(
    apiUrl + "/sacco/matatu/profile/" + encodeURIComponent(walletId),
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(function (response) {
    return response.json();
  });
}

/** GET sacco/{sacco_id}/parcels/revenue */
export function fetchParcelRevenueBySacco(saccoId: string) {
  return fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/parcels/revenue`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => response.json());
}

export function fetchParcelSummaryByDate(saccoId: string, date?: string) {
  return fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/parcels/summary-by-date`,
    {
      method: "POST",
      headers: appendHeaders(),
      body: JSON.stringify({
        date,
      }),
    },
  ).then(async (response) => response.json());
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].trim());
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(header);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();
  const plainMatch = /filename=([^;\n]+)/i.exec(header);
  return plainMatch?.[1]?.trim().replace(/"/g, "") ?? null;
}

function triggerFileDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** GET sacco/{sacco_id}/parcels/report/summary-by-date — downloads report file for date range */
export async function downloadParcelReportSummaryByDate(
  saccoId: string,
  startDate: string,
  endDate: string,
): Promise<void> {
  const params = new URLSearchParams({ startDate, endDate });
  const response = await fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/parcels/report/summary-by-date?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getDashboardApiToken(),
      },
    },
  );

  if (!response.ok) {
    let message = `Failed to download parcel report (${response.status})`;
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        const errJson = await response.json();
        message =
          errJson?.message || errJson?.error || errJson?.detail || message;
      } catch {
        // keep default message
      }
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(
      response.headers.get("Content-Disposition"),
    ) ?? `parcel-report-${startDate}-to-${endDate}`;

  triggerFileDownload(blob, filename);
}

/** GET sacco/{sacco_id}/parcels/top-routes */
export function fetchParcelTopRoutesBySacco(saccoId: string) {
  return fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/parcels/top-routes`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => response.json());
}

/** POST /sacco/{sacco_id}/stage-collection/summary-by-date — date sent in request body. */
export function fetchStageCollectionSummary(
  saccoId: string,
  date: string,
): Promise<StageCollectionSummary> {
  return fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/stage-collection/summary-by-date`,
    {
      method: "POST",
      headers: appendHeaders(),
      body: JSON.stringify({ date }),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to fetch stage collection summary (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch { /* keep default */ }
      throw new Error(message);
    }
    return response.json().then((res) => {
      const payload = (res?.body?.payload ?? res?.payload ?? res) as StageCollectionSummary;
      return payload;
    });
  });
}

/** GET /stage-collection/{sacco_id}/revenue */
export function fetchStageCollectionRevenue(
  saccoId: string,
): Promise<StageCollectionRevenue> {
  return fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/stage-collection/revenue`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to fetch stage collection revenue (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch { /* keep default */ }
      throw new Error(message);
    }
    return response.json().then((res) => {
      const item = Array.isArray(res) ? res[0] : res;
      return (item?.payload ?? item) as StageCollectionRevenue;
    });
  });
}

/** GET /stage-collection/{sacco_id}/top-stages — collections count/amount per matatu stage. */
export function fetchStageCollectionTopStages(
  saccoId: string,
): Promise<StageCollectionTopStages> {
  return fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/stage-collection/top-routes`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to fetch top stages (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch { /* keep default */ }
      throw new Error(message);
    }
    return response.json().then((res) => {
      const payload = res?.payload ?? res;
      return (typeof payload === "object" && payload !== null && !Array.isArray(payload)
        ? payload
        : {}) as StageCollectionTopStages;
    });
  });
}

/** GET /stage-collection/{sacco_id}/report/summary-by-date?startDate=&endDate= — downloads report file. */
export async function downloadStageCollectionReport(
  saccoId: string,
  startDate: string,
  endDate: string,
): Promise<void> {
  const params = new URLSearchParams({ startDate, endDate });
  const response = await fetchWithAuth(
    `${apiUrl}/sacco/${encodeURIComponent(saccoId)}/stage-collection/report/summary-by-date?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + getDashboardApiToken(),
      },
    },
  );

  if (!response.ok) {
    let message = `Failed to download stage collection report (${response.status})`;
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || errJson?.detail || message;
      } catch { /* keep default */ }
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
    `stage-collections-report-${startDate}-to-${endDate}`;

  triggerFileDownload(blob, filename);
}
